import { INTERVALLE_PING_MS, estMsgServeur, type MsgClient, type MsgServeur } from '@camellie/shared';

type Ecouteur = (msg: MsgServeur) => void;
type EcouteurEtat = (connecte: boolean) => void;

const BACKOFF_MIN_MS = 500;
const BACKOFF_MAX_MS = 20_000;

/**
 * WebSocket vers le Durable Object de la famille.
 *
 * Sur iPhone, le socket est coupe des que l'app passe en arriere-plan ou que
 * l'ecran s'eteint, plusieurs fois par jour. La reconnexion n'est donc pas un
 * cas d'erreur, c'est le fonctionnement normal : backoff exponentiel avec
 * jitter pour ne pas taper en rafale, et reconnexion immediate des que l'app
 * redevient visible ou que le reseau revient.
 */
export class Socket {
  private ws: WebSocket | null = null;
  private tentatives = 0;
  private minuterieReconnexion: number | null = null;
  private minuteriePing: number | null = null;
  private ferme = false;

  private readonly ecouteurs = new Set<Ecouteur>();
  private readonly ecouteursEtat = new Set<EcouteurEtat>();

  /** maj_le le plus recent connu : permet de ne resynchroniser qu'un delta. */
  private depuis: string | null = null;

  constructor() {
    document.addEventListener('visibilitychange', this.auReveil);
    window.addEventListener('online', this.auReveil);
  }

  private auReveil = (): void => {
    if (document.visibilityState !== 'visible') return;
    if (this.estOuvert()) {
      // Le socket parait ouvert, mais iOS a pu le geler sans nous prevenir :
      // un ping verifie qu'il repond encore vraiment.
      this.envoyer({ t: 'ping' });
      return;
    }
    this.tentatives = 0;
    this.connecter();
  };

  surMessage(f: Ecouteur): () => void {
    this.ecouteurs.add(f);
    return () => this.ecouteurs.delete(f);
  }

  surEtat(f: EcouteurEtat): () => void {
    this.ecouteursEtat.add(f);
    return () => this.ecouteursEtat.delete(f);
  }

  estOuvert(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  noterHorloge(maj_le: string): void {
    if (!this.depuis || maj_le > this.depuis) this.depuis = maj_le;
  }

  connecter(): void {
    this.ferme = false;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const protocole = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(protocole + '//' + location.host + '/api/ws');
    this.ws = ws;

    ws.onopen = () => {
      this.tentatives = 0;
      this.prevenirEtat(true);
      // `depuis` non nul = on revient d'une coupure : le serveur ne renvoie
      // que ce qui a change, pas toute la liste.
      this.envoyer({ t: 'bonjour', depuis: this.depuis });
      this.demarrerPing();
    };

    ws.onmessage = (ev) => {
      let msg: unknown;
      try {
        msg = JSON.parse(String(ev.data));
      } catch {
        return;
      }
      if (!estMsgServeur(msg)) return;
      for (const f of this.ecouteurs) f(msg);
    };

    ws.onclose = () => {
      this.arreterPing();
      this.prevenirEtat(false);
      if (!this.ferme) this.planifierReconnexion();
    };

    ws.onerror = () => {
      // onclose suit toujours : rien a faire ici, sinon on planifierait la
      // reconnexion deux fois.
    };
  }

  private planifierReconnexion(): void {
    if (this.minuterieReconnexion !== null) return;
    const base = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** this.tentatives);
    // Jitter : deux telephones qui perdent le reseau en meme temps (meme
    // wifi) ne doivent pas revenir exactement a la meme milliseconde.
    const delai = base / 2 + Math.random() * (base / 2);
    this.tentatives++;
    this.minuterieReconnexion = window.setTimeout(() => {
      this.minuterieReconnexion = null;
      this.connecter();
    }, delai);
  }

  private demarrerPing(): void {
    this.arreterPing();
    this.minuteriePing = window.setInterval(() => {
      if (this.estOuvert()) this.envoyer({ t: 'ping' });
    }, INTERVALLE_PING_MS);
  }

  private arreterPing(): void {
    if (this.minuteriePing !== null) {
      clearInterval(this.minuteriePing);
      this.minuteriePing = null;
    }
  }

  private prevenirEtat(connecte: boolean): void {
    for (const f of this.ecouteursEtat) f(connecte);
  }

  /** Retourne false si le message n'a pas pu partir (socket ferme). */
  envoyer(msg: MsgClient): boolean {
    if (!this.estOuvert()) return false;
    try {
      this.ws!.send(JSON.stringify(msg));
      return true;
    } catch {
      return false;
    }
  }

  fermer(): void {
    this.ferme = true;
    this.arreterPing();
    if (this.minuterieReconnexion !== null) {
      clearTimeout(this.minuterieReconnexion);
      this.minuterieReconnexion = null;
    }
    this.ws?.close(1000, 'Deconnexion');
    this.ws = null;
  }
}
